import React, { useState, useEffect } from 'react'
import {
  Container,
  Header,
  SpaceBetween,
  Wizard,
  Box,
  Button,
  Alert,
  StatusIndicator,
  ColumnLayout,
  Badge,
  Checkbox,
  ProgressBar
} from '@cloudscape-design/components'
import { usePageHeader } from '../../../providers/PageHeaderProvider'
import { useAmplifyAuth } from '../../../providers/AmplifyAuthProvider'
import { useAction } from 'convex/react'
import { api } from '../../../../convex/_generated/api'

interface SiteWiseAsset {
  assetId: string
  assetName: string
  assetModelId: string
  assetProperties: any[]
}

interface AssetDiscoveryResult {
  allAssets: SiteWiseAsset[]
  topLevelAssets: SiteWiseAsset[]
  assetParentMap: Record<string, string>
  assetModels: any[]
  totalAssets: number
}

export default function AssetDiscoveryPage() {
  const { setHeaderConfig } = usePageHeader()
  const { sessionId } = useAmplifyAuth()
  const [activeStepIndex, setActiveStepIndex] = useState(0)
  const [connectionStatus, setConnectionStatus] = useState<any>(null)
  const [discoveredAssets, setDiscoveredAssets] = useState<any>(null)
  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set())
  const [isLoading, setIsLoading] = useState(false)
  const [syncInProgress, setSyncInProgress] = useState(false)
  const [syncResults, setSyncResults] = useState<any>(null)

  // Convex actions
  const testConnection = useAction(api.components.assets.index.discoverAllAssets)
  const discoverAllAssets = useAction(api.components.assets.index.discoverAllAssetsWithHierarchy)
  const syncAssets = useAction(api.components.assets.index.syncSelectedAssets)

  useEffect(() => {
    setHeaderConfig({
      title: 'Asset Discovery',
      description: 'Discover and import assets from AWS IoT SiteWise into your Alterra IoT platform.',
      actions: []
    })
  }, [])

  // Helper function to determine hierarchy level from asset model
  const determineHierarchyLevel = (assetModelName: string): 'factory' | 'production_line' | 'equipment_component' => {
    const HIERARCHY_MAPPING = [
      {
        level: 'factory' as const,
        assetModelPatterns: ['Factory_Model', 'Plant_Model', 'Site_Model']
      },
      {
        level: 'production_line' as const, 
        assetModelPatterns: ['Production_Line_Model', 'Line_Model']
      },
      {
        level: 'equipment_component' as const,
        assetModelPatterns: [
          'Extruder_Model', 'Feeder_Model', 'Barrel_Model', 'Motor_Model',
          'Pump_Model', 'Die_Model', 'Adaptor_Model', 'SingleScrew_Model',
          'Kneader_Model', 'ScreenChanger_Model', 'Up_Stream_Model', 
          'Main_Stream_Model', 'Other_Model', 'TANK_MODEL', 'Tank_Model'
        ]
      }
    ]

    for (const mapping of HIERARCHY_MAPPING) {
      if (mapping.assetModelPatterns.some(pattern => 
        assetModelName.toLowerCase().includes(pattern.toLowerCase()) ||
        pattern.toLowerCase().includes(assetModelName.toLowerCase())
      )) {
        return mapping.level
      }
    }
    return 'equipment_component' // Default fallback
  }

  const getHierarchyBadgeColor = (level: string) => {
    switch (level) {
      case 'factory': return 'blue'
      case 'production_line': return 'green'
      case 'equipment_component': return 'grey'
      default: return 'grey'
    }
  }

  const getHierarchyIcon = (level: string) => {
    switch (level) {
      case 'factory': return '🏭'
      case 'production_line': return '🏗️'
      case 'equipment_component': return '⚙️'
      default: return '📦'
    }
  }

  const handleTestConnection = async () => {
    if (!sessionId) return
    
    setIsLoading(true)
    try {
      // Test connection by attempting to discover assets
      const result = await testConnection({ sessionId })
      
      if (result.success) {
        setConnectionStatus({
          success: true,
          region: 'us-east-1',
          assetsCount: result.assetsCount,
          modelsCount: result.assetModels?.length || 0
        })
      } else {
        setConnectionStatus({
          success: false,
          error: result.error || 'Failed to connect to SiteWise',
          region: 'us-east-1'
        })
      }
    } catch (error: any) {
      setConnectionStatus({
        success: false,
        error: error.message || 'Failed to connect to SiteWise',
        region: 'us-east-1'
      })
    }
    setIsLoading(false)
  }

  const handleDiscoverAssets = async () => {
    if (!sessionId) return
    
    setIsLoading(true)
    try {
      const result = await discoverAllAssets({ sessionId })
      
      if (result.success) {
        // Transform the result to match our expected format
        setDiscoveredAssets({
          allAssets: result.assets || [],
          assetModels: result.assetModels || [],
          totalAssets: result.assetsCount || 0,
          success: true
        })
      } else {
        console.error('Asset discovery failed:', result.error)
        setDiscoveredAssets({
          success: false,
          error: result.error
        })
      }
    } catch (error: any) {
      console.error('Asset discovery failed:', error)
      setDiscoveredAssets({
        success: false,
        error: error.message
      })
    }
    setIsLoading(false)
  }

  const handleAssetToggle = (assetId: string, checked: boolean) => {
    const newSelected = new Set(selectedAssets)
    if (checked) {
      newSelected.add(assetId)
    } else {
      newSelected.delete(assetId)
    }
    setSelectedAssets(newSelected)
  }

  const handleSync = async () => {
    if (!sessionId || selectedAssets.size === 0) return

    setSyncInProgress(true)
    try {
      // Flatten all assets including children for sync
      const flattenAssets = (assets) => {
        const flattened = []
        for (const asset of assets) {
          flattened.push(asset)
          if (asset.children && asset.children.length > 0) {
            flattened.push(...flattenAssets(asset.children))
          }
        }
        return flattened
      }
      
      const allFlatAssets = flattenAssets(discoveredAssets.allAssets)
      
      const assetsToSync = allFlatAssets
        .filter(asset => selectedAssets.has(asset.assetId))
        .map(asset => {
          const assetModel = discoveredAssets?.assetModels.find(m => m.id === asset.assetModelId)
          const assetModelName = assetModel?.name || asset.assetModelName || 'Unknown'
          const hierarchyLevel = asset.hierarchyLevel || determineHierarchyLevel(assetModelName)
          
          return {
            assetId: asset.assetId,
            name: asset.assetName,
            assetModelId: asset.assetModelId,
            hierarchyLevel,
            properties: (asset.assetProperties || []).map(prop => ({
              id: prop.id,
              name: prop.name,
              dataType: prop.dataType,
              unit: prop.unit,
              alias: prop.alias,
              type: prop.type
            }))
          }
        })

      const result = await syncAssets({
        sessionId,
        assets: assetsToSync
      })
      
      if (result.success) {
        setSyncResults({
          success: true,
          factoriesCreated: result.factoriesCreated,
          productionLinesCreated: result.productionLinesCreated,
          equipmentComponentsCreated: result.equipmentComponentsCreated,
          message: `Successfully synced ${result.factoriesCreated + result.productionLinesCreated + result.equipmentComponentsCreated} assets`
        })
      } else {
        setSyncResults({ 
          success: false, 
          error: result.error || 'Sync failed' 
        })
      }
    } catch (error: any) {
      setSyncResults({ 
        success: false, 
        error: error.message || 'Import failed' 
      })
    }
    setSyncInProgress(false)
  }

  const steps = [
    {
      title: 'Test SiteWise Connection',
      content: (
        <Container header={<Header variant="h3">AWS IoT SiteWise Connection</Header>}>
          <SpaceBetween size="m">
            <Box variant="p">
              First, we'll test the connection to your AWS IoT SiteWise environment to ensure we can access your assets.
            </Box>
            
            <Button 
              variant="primary"
              onClick={handleTestConnection}
              loading={isLoading}
              disabled={!!connectionStatus}
            >
              {connectionStatus ? 'Test Connection Again' : 'Test Connection'}
            </Button>

            {connectionStatus && (
              <Alert 
                type={connectionStatus.success ? 'success' : 'error'}
                header={connectionStatus.success ? 'Connection Successful' : 'Connection Failed'}
              >
                {connectionStatus.success ? (
                  <SpaceBetween size="xs">
                    <div>✅ Successfully connected to AWS IoT SiteWise in {connectionStatus.region}</div>
                    <div>📊 Found {connectionStatus.assetsCount} assets and {connectionStatus.modelsCount} models</div>
                  </SpaceBetween>
                ) : (
                  <div>❌ {connectionStatus.error}</div>
                )}
              </Alert>
            )}
          </SpaceBetween>
        </Container>
      ),
      isOptional: false
    },
    {
      title: 'Discover Assets',
      content: (
        <Container header={<Header variant="h3">Asset Discovery</Header>}>
          <SpaceBetween size="m">
            <Box variant="p">
              Scan your SiteWise environment to discover all available assets and asset models.
            </Box>
            
            <Button 
              variant="primary"
              onClick={handleDiscoverAssets}
              loading={isLoading}
              disabled={!connectionStatus?.success || !!discoveredAssets}
            >
              {discoveredAssets ? 'Discover Assets Again' : 'Discover Assets'}
            </Button>

            {discoveredAssets && (
              <Alert type="success" header={`Discovered ${discoveredAssets.totalAssets} Assets`}>
                <ColumnLayout columns={2}>
                  <div>
                    <Box variant="awsui-key-label">Total Assets</Box>
                    <div>{discoveredAssets.totalAssets}</div>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Asset Models</Box>
                    <div>{discoveredAssets.assetModels.length}</div>
                  </div>
                </ColumnLayout>
              </Alert>
            )}
          </SpaceBetween>
        </Container>
      ),
      isOptional: false
    },
    {
      title: 'Select Assets to Import',
      content: (
        <Container header={<Header variant="h3">Select Assets for Import</Header>}>
          <SpaceBetween size="m">
            <Box variant="p">
              Choose which assets you want to import into your Alterra IoT platform.
            </Box>

            {discoveredAssets?.success ? (
              <div>
                <SpaceBetween size="m">
                  {/* Asset selection controls */}
                  <Box>
                    <SpaceBetween direction="horizontal" size="xs">
                      <Checkbox
                        checked={selectedAssets.size === discoveredAssets.allAssets.length && discoveredAssets.allAssets.length > 0}
                        indeterminate={selectedAssets.size > 0 && selectedAssets.size < discoveredAssets.allAssets.length}
                        onChange={({ detail }) => {
                          if (detail.checked) {
                            setSelectedAssets(new Set(discoveredAssets.allAssets.map(a => a.assetId)))
                          } else {
                            setSelectedAssets(new Set())
                          }
                        }}
                      >
                        Select All Assets
                      </Checkbox>
                      <StatusIndicator type="info">
                        {selectedAssets.size} of {discoveredAssets.allAssets.length} assets selected
                      </StatusIndicator>
                    </SpaceBetween>
                  </Box>

                  {/* Asset List - Render hierarchically */}
                  <div style={{ border: '1px solid #d5dbdb', borderRadius: '8px', padding: '16px' }}>
                    {discoveredAssets.allAssets.map(asset => {
                      const renderAssetWithChildren = (asset: any, level: number = 0) => {
                        const hierarchyLevel = asset.hierarchyLevel || 'equipment_component'
                        const isSelected = selectedAssets.has(asset.assetId)
                        
                        return (
                          <div key={asset.assetId}>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              padding: '8px 0',
                              marginLeft: `${level * 20}px`,
                              borderBottom: level === 0 ? '1px solid #eaeded' : 'none'
                            }}>
                              <div style={{ marginRight: '12px' }}>
                                <Checkbox
                                  checked={isSelected}
                                  onChange={({ detail }) => handleAssetToggle(asset.assetId, detail.checked)}
                                />
                              </div>
                              <div style={{ flexGrow: 1 }}>
                                <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                                  <span style={{ 
                                    fontWeight: level === 0 ? '600' : '500',
                                    color: level > 0 ? '#666' : 'inherit'
                                  }}>
                                    {getHierarchyIcon(hierarchyLevel)} {asset.assetName}
                                  </span>
                                  <Badge color={getHierarchyBadgeColor(hierarchyLevel)} size="small">
                                    {hierarchyLevel.replace('_', ' ')}
                                  </Badge>
                                  <Badge color="grey" size="small">
                                    {asset.assetProperties?.length || 0} props
                                  </Badge>
                                  <span style={{ fontSize: '11px', color: '#888' }}>
                                    {asset.assetModelName || 'Unknown Model'}
                                  </span>
                                </SpaceBetween>
                              </div>
                            </div>
                            {/* Render children recursively */}
                            {asset.children && asset.children.length > 0 && (
                              <div>
                                {asset.children.map((child: any) => renderAssetWithChildren(child, level + 1))}
                              </div>
                            )}
                          </div>
                        )
                      }
                      
                      return renderAssetWithChildren(asset)
                    })}
                  </div>

                  {/* Selected assets summary */}
                  {selectedAssets.size > 0 && (
                    <div style={{ 
                      marginTop: '16px',
                      backgroundColor: '#f9f9f9',
                      padding: '12px',
                      borderRadius: '4px'
                    }}>
                      <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
                        Selected Assets ({selectedAssets.size}):
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {/* Flatten assets for summary display */}
                        {discoveredAssets.allAssets.reduce((flattened: any[], asset: any) => {
                          const flatten = (a: any): any[] => {
                            const result = [a]
                            if (a.children) {
                              a.children.forEach((child: any) => result.push(...flatten(child)))
                            }
                            return result
                          }
                          return [...flattened, ...flatten(asset)]
                        }, []).filter((asset: any) => selectedAssets.has(asset.assetId)).map((asset: any) => {
                          const hierarchyLevel = asset.hierarchyLevel || 'equipment_component'
                          
                          return (
                            <div key={asset.assetId} style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '2px 6px',
                              backgroundColor: 'white',
                              border: '1px solid #d5dbdb',
                              borderRadius: '12px',
                              fontSize: '12px'
                            }}>
                              <span style={{ marginRight: '4px' }}>
                                {getHierarchyIcon(hierarchyLevel)}
                              </span>
                              <span>{asset.assetName}</span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </SpaceBetween>
              </div>
            ) : discoveredAssets?.error ? (
              <Alert type="error" header="Asset Discovery Failed">
                {discoveredAssets.error}
              </Alert>
            ) : (
              <Alert type="warning">
                Please complete asset discovery first before selecting assets to import.
              </Alert>
            )}
          </SpaceBetween>
        </Container>
      ),
      isOptional: false
    },
    {
      title: 'Import & Sync',
      content: (
        <Container header={<Header variant="h3">Import Assets</Header>}>
          <SpaceBetween size="m">
            <Box variant="p">
              Import the selected assets into your Alterra IoT platform and establish synchronization with SiteWise.
            </Box>

            {syncResults ? (
              syncResults.success ? (
                <Alert type="success" header="Assets Successfully Imported">
                  <SpaceBetween size="m">
                    <div>
                      <Box variant="awsui-key-label">Assets Imported</Box>
                      <div>{syncResults.importedCount}</div>
                    </div>
                    <div>{syncResults.message}</div>
                  </SpaceBetween>
                </Alert>
              ) : (
                <Alert type="error" header="Import Failed">
                  {syncResults.error}
                </Alert>
              )
            ) : (
              <div>
                <Button 
                  variant="primary"
                  onClick={handleSync}
                  loading={syncInProgress}
                  disabled={selectedAssets.size === 0}
                >
                  {syncInProgress ? 'Importing Assets...' : `Import ${selectedAssets.size} Selected Assets`}
                </Button>

                {syncInProgress && (
                  <div style={{ marginTop: '16px' }}>
                    <SpaceBetween size="s">
                      <Box variant="awsui-key-label">Importing Assets to Database...</Box>
                      <ProgressBar variant="indeterminate" />
                    </SpaceBetween>
                  </div>
                )}
              </div>
            )}
          </SpaceBetween>
        </Container>
      ),
      isOptional: false
    }
  ]

  const canProgress = (stepIndex: number) => {
    switch (stepIndex) {
      case 0: return connectionStatus?.success
      case 1: return !!discoveredAssets
      case 2: return selectedAssets.size > 0
      case 3: return !!syncResults?.success
      default: return true
    }
  }

  return (
    <Container>
      <Wizard
        steps={steps}
        activeStepIndex={activeStepIndex}
        onNavigate={({ detail }) => setActiveStepIndex(detail.requestedStepIndex)}
        i18nStrings={{
          stepNumberLabel: stepNumber => `Step ${stepNumber}`,
          collapsedStepsLabel: (stepNumber, totalStepNumber) =>
            `Step ${stepNumber} of ${totalStepNumber}`,
          navigationAriaLabel: 'Steps',
          cancelButton: 'Cancel',
          previousButton: 'Previous',
          nextButton: 'Next',
          submitButton: 'Complete Import',
          optional: 'Optional'
        }}
        onCancel={() => window.history.back()}
        onSubmit={() => {
          console.log('Asset discovery completed')
          // Navigate back to assets page
          window.location.href = '/assets'
        }}
        allowSkipTo={false}
        isLoadingNextStep={isLoading || syncInProgress}
      />
    </Container>
  )
}